import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, videos, featuredCreators, testimonials, formatViews } from '@/mocks/videos';

const SITE_ORIGIN = 'http://saitemold.com';

export default function Home() {
  const { t } = useTranslation();
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        name: t('siteName'),
        url: SITE_ORIGIN,
        description: t('siteTagline'),
        potentialAction: {
          '@type': 'SearchAction',
          target: `${SITE_ORIGIN}/search?q={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'WebPage',
        '@id': `${SITE_ORIGIN}/`,
        url: `${SITE_ORIGIN}/`,
        name: `${t('siteName')} - ${t('siteTagline')}`,
        description: t('siteTagline'),
        isPartOf: {
          '@id': `${SITE_ORIGIN}/#website`,
        },
      },
      {
        '@type': 'Organization',
        name: t('siteName'),
        url: SITE_ORIGIN,
        description: t('siteTagline'),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[600px] md:min-h-[700px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20creative%20video%20content%20visualization%20with%20floating%20geometric%20shapes%20and%20warm%20amber%20golden%20light%20particles%2C%20cinematic%20atmosphere%2C%20soft%20gradient%20background%20from%20warm%20cream%20to%20light%20amber%2C%20editorial%20digital%20art%20style%20with%20minimalist%20aesthetic&width=1600&height=900&seq=hero-bg-01&orientation=landscape"
            alt="国产91视频主视觉背景"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />
        </div>

        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="w-2 h-2 rounded-full bg-accent-500" />
            <span className="text-sm text-background-100 tracking-wider uppercase">
              国内原创视频发现平台
            </span>
          </div>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold font-heading text-background-50 leading-tight mb-6">
            {t('hero.title')}
          </h1>
          <p className="text-lg md:text-xl text-background-100 max-w-2xl mx-auto mb-10 leading-relaxed">
            {t('hero.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/category/funny"
              className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-8 py-3.5 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {t('hero.ctaPrimary')}
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line" />
              </span>
            </Link>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 bg-background-50/10 backdrop-blur-sm text-background-50 border border-background-50/30 px-8 py-3.5 rounded-full text-sm font-medium hover:bg-background-50/20 transition-colors whitespace-nowrap"
            >
              {t('hero.ctaSecondary')}
            </Link>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 md:py-24 bg-background-100">
        <div className="px-4 md:px-8 max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-foreground-900 mb-4">
              {t('categories.title')}
            </h2>
            <p className="text-foreground-600 text-base md:text-lg">
              {t('categories.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className="group relative aspect-square rounded-2xl overflow-hidden"
              >
                <img
                  src={cat.coverImage}
                  alt={cat.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6">
                  <h3 className="text-xl md:text-2xl font-bold text-background-50 mb-2">
                    {cat.name}
                  </h3>
                  <p className="text-sm text-background-100/80 mb-3 max-w-[200px]">
                    {cat.description}
                  </p>
                  <span className="text-xs text-background-100/60">
                    {cat.videoCount.toLocaleString()} 个视频
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Trending Videos Section */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="px-4 md:px-8 max-w-7xl mx-auto">
          <div className="mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-foreground-900 mb-3">
              {t('trending.title')}
            </h2>
            <p className="text-foreground-600 text-base md:text-lg">
              {t('trending.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {videos.slice(0, 6).map((video) => (
              <article key={video.id} className="group">
                <Link to={`/video/${video.id}`} className="block">
                  <div className="relative aspect-video rounded-xl overflow-hidden mb-4 border border-background-200/70">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute bottom-2 right-2 bg-black/70 text-background-50 text-xs px-2 py-1 rounded">
                      {video.duration}
                    </div>
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                      <span className="w-12 h-12 rounded-full bg-background-50/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <i className="ri-play-fill text-xl text-primary-500" />
                      </span>
                    </div>
                  </div>
                  <h3 className="text-base md:text-lg font-medium text-foreground-900 leading-snug mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {video.title}
                  </h3>
                  <div className="flex items-center gap-3 text-xs text-foreground-500">
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-user-line" />
                      </span>
                      {video.creator}
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-eye-line" />
                      </span>
                      {formatViews(video.views)}
                    </span>
                  </div>
                </Link>
              </article>
            ))}
          </div>

          <div className="text-center mt-10 md:mt-14">
            <Link
              to="/category/funny"
              className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
            >
              查看更多视频
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Creators Section */}
      <section className="py-16 md:py-24 bg-background-100">
        <div className="px-4 md:px-8 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-foreground-900 mb-6">
                {t('creators.title')}
              </h2>
              <p className="text-foreground-600 text-base md:text-lg leading-relaxed mb-8 max-w-lg">
                {t('creators.subtitle')}
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 bg-accent-500 text-background-50 px-6 py-3 rounded-full text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap mb-10"
              >
                {t('creators.cta')}
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line" />
                </span>
              </Link>

              <div className="flex gap-12">
                <div>
                  <p className="text-3xl md:text-4xl font-bold text-foreground-900">
                    500+
                  </p>
                  <p className="text-sm text-foreground-500 mt-1">
                    签约创作者
                  </p>
                </div>
                <div>
                  <p className="text-3xl md:text-4xl font-bold text-foreground-900">
                    2000万+
                  </p>
                  <p className="text-sm text-foreground-500 mt-1">
                    日活跃用户
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {featuredCreators.map((creator) => (
                <div
                  key={creator.id}
                  className="flex items-center gap-4 bg-background-50 rounded-xl p-4 border border-background-200/70"
                >
                  <img
                    src={creator.avatar}
                    alt={creator.name}
                    className="w-14 h-14 md:w-16 md:h-16 rounded-full object-cover flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-base font-medium text-foreground-900 truncate">
                        {creator.name}
                      </h4>
                      <span className="text-xs bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded-full whitespace-nowrap">
                        {creator.location}
                      </span>
                    </div>
                    <p className="text-sm text-foreground-500 line-clamp-1">
                      {creator.description}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0 hidden sm:block">
                    <p className="text-sm font-semibold text-foreground-900">
                      {creator.followers}
                    </p>
                    <p className="text-xs text-foreground-500">
                      {t('creators.followers')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="px-4 md:px-8 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="relative aspect-square rounded-3xl overflow-hidden bg-accent-100 max-w-md mx-auto lg:mx-0">
              <img
                src="https://readdy.ai/api/search-image?query=Happy%20young%20Chinese%20couple%20watching%20video%20content%20together%20on%20sofa%20with%20warm%20natural%20lighting%2C%20cozy%20modern%20living%20room%2C%20genuine%20smiles%2C%20relaxed%20atmosphere%2C%20editorial%20lifestyle%20photography&width=600&height=600&seq=testimonial-img-01&orientation=squarish"
                alt="用户欣赏视频内容"
                className="w-full h-full object-cover"
              />
            </div>

            <div>
              <span className="text-sm text-accent-600 font-medium mb-4 block">
                {t('testimonial.label')}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-foreground-900 mb-8">
                {t('testimonial.title')}
              </h2>

              <div className="min-h-[180px]">
                <blockquote className="mb-8">
                  <p className="text-lg md:text-xl text-foreground-700 leading-relaxed">
                    &ldquo;{testimonials[activeTestimonial].content}&rdquo;
                  </p>
                </blockquote>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-0.5 bg-accent-500" />
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">
                      {testimonials[activeTestimonial].author}
                    </p>
                    <p className="text-xs text-foreground-500">
                      {testimonials[activeTestimonial].role}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 mt-8">
                <button
                  type="button"
                  onClick={() =>
                    setActiveTestimonial((prev) =>
                      prev === 0 ? testimonials.length - 1 : prev - 1
                    )
                  }
                  className="w-10 h-10 flex items-center justify-center rounded-full border border-background-300 text-foreground-500 hover:border-foreground-400 hover:text-foreground-700 transition-colors"
                  aria-label="上一条评价"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line" />
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setActiveTestimonial((prev) =>
                      prev === testimonials.length - 1 ? 0 : prev + 1
                    )
                  }
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
                  aria-label="下一条评价"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line" />
                  </span>
                </button>
                <span className="text-sm text-foreground-500 ml-2">
                  {activeTestimonial + 1} / {testimonials.length}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative min-h-[400px] md:min-h-[500px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Young%20Chinese%20content%20creators%20collaborating%20in%20modern%20creative%20studio%20with%20cameras%20and%20lighting%20equipment%2C%20warm%20ambient%20lighting%2C%20collaborative%20energy%2C%20professional%20creative%20atmosphere%2C%20editorial%20lifestyle%20photography&width=1600&height=900&seq=cta-bg-01&orientation=landscape"
            alt="创作者社区"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/60" />
        </div>

        <div className="relative z-10 px-4 md:px-8 max-w-7xl mx-auto w-full">
          <div className="max-w-xl">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-background-50 mb-6">
              {t('cta.title')}
            </h2>
            <Link
              to="/category/funny"
              className="inline-flex items-center gap-2 bg-background-50 text-primary-700 px-8 py-3.5 rounded-full text-sm font-medium hover:bg-background-100 transition-colors whitespace-nowrap mb-6"
            >
              {t('cta.button')}
              <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-500">
                <i className="ri-arrow-right-line text-xs text-background-50" />
              </span>
            </Link>
            <p className="text-background-100 text-sm md:text-base">
              {t('cta.description')}
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
